import { useState, useEffect, useRef } from 'react';
import axios, { AxiosError } from 'axios';
import axiosInstance from 'utils/axios';
import {
  AiOperationCosts,
  DEFAULT_AI_OPERATION_COSTS,
  parseAiOperationCosts
} from 'constants/aiCredits';

interface TenantInfo {
  tenantName: string;
  tenantLogo: string;
  tenantPrivacyUrl: string;
  tenantTermUrl: string;
  tenantFacebookUrl: string;
  tenantXUrl: string;
  tenantLinkedinUrl: string;
  tenantSiteLanguages: string[];
  aiOperationCosts: AiOperationCosts;
}

// Default logo fallback path
const DEFAULT_LOGO_PATH = '/static/images/new-rec-logos.svg';

const DEFAULT_TENANT_INFO: TenantInfo = {
  tenantName: 'Skillio',
  tenantLogo: '',
  tenantPrivacyUrl: '',
  tenantTermUrl: '',
  tenantFacebookUrl: '',
  tenantXUrl: '',
  tenantLinkedinUrl: '',
  tenantSiteLanguages: [],
  aiOperationCosts: DEFAULT_AI_OPERATION_COSTS
};

const buildTenantInfoFromMetaTags = (): TenantInfo => {
  if (typeof window === 'undefined') {
    return DEFAULT_TENANT_INFO;
  }

  const tenantSiteLanguages = parseLanguages(
    getMetaContent('tenant-site-languages')
  );

  return {
    tenantName: getMetaContent('tenant-name') || DEFAULT_TENANT_INFO.tenantName,
    tenantLogo: getMetaContent('tenant-logo') || '',
    tenantPrivacyUrl: getMetaContent('tenant-privacy-url') || '',
    tenantTermUrl: getMetaContent('tenant-term-url') || '',
    tenantFacebookUrl: getMetaContent('facebook-url') || '',
    tenantXUrl: getMetaContent('x-url') || '',
    tenantLinkedinUrl: getMetaContent('linkedin-url') || '',
    tenantSiteLanguages:
      tenantSiteLanguages.length > 0
        ? tenantSiteLanguages
        : process.env.NODE_ENV === 'development'
          ? ['en', 'nl']
          : [],
    aiOperationCosts: DEFAULT_AI_OPERATION_COSTS
  };
};

let cachedTenantInfo: TenantInfo | null = null;
let tenantRequestPromise: Promise<TenantInfo | null> | null = null;

// Helper function to get meta tag content - handles multiple meta tags with same name
const getMetaContent = (metaName: string): string => {
  // Try to get all meta tags with this name
  const metaTags = document.querySelectorAll(`meta[name="${metaName}"]`);

  if (!metaTags || metaTags.length === 0) {
    return '';
  }

  // If multiple meta tags exist, prioritize the one with non-empty content
  for (let i = 0; i < metaTags.length; i++) {
    const content = metaTags[i].getAttribute('content');
    if (content && content.trim() !== '') {
      return content;
    }
  }

  // If all are empty or no content attribute, return empty string
  return metaTags[0].getAttribute('content') || '';
};

// Parse languages string to array
const parseLanguages = (languages: string | string[] | null | undefined): string[] => {
  if (Array.isArray(languages)) {
    return languages
      .map((lang) => String(lang).trim())
      .filter((lang) => lang.length > 0);
  }

  if (!languages || languages.trim() === '') {
    return [];
  }

  const result = languages.split(',').map((lang) => lang.trim());
  return result;
};

// Fetch tenant info from API
const fetchTenantFromApi = async (
  signal?: AbortSignal
): Promise<TenantInfo | null> => {
  try {
    const response = await axiosInstance.get('/tenant', {
      signal
    });

    if (response?.data && response.data.length > 0) {
      const tenant = response.data[0];

      const parsedLanguages = parseLanguages(tenant.site_languages || '');

      // Update meta tags if tenant info is found
      if (tenant.name) {
        document
          .querySelector('meta[name="tenant-name"]')
          ?.setAttribute('content', tenant.name);
      }

      if (tenant.logo) {
        document
          .querySelector('meta[name="tenant-logo"]')
          ?.setAttribute('content', tenant.logo);
      }

      if (tenant.privacy_statement) {
        document
          .querySelector('meta[name="tenant-privacy-url"]')
          ?.setAttribute('content', tenant.privacy_statement);
      }

      if (tenant.general_terms) {
        document
          .querySelector('meta[name="tenant-term-url"]')
          ?.setAttribute('content', tenant.general_terms);
      }

      if (tenant.social_media_links?.facebook) {
        document
          .querySelector('meta[name="facebook-url"]')
          ?.setAttribute('content', tenant.social_media_links.facebook);
      }

      if (tenant.social_media_links?.twitter) {
        document
          .querySelector('meta[name="x-url"]')
          ?.setAttribute('content', tenant.social_media_links.twitter);
      }

      if (tenant.social_media_links?.linkedin) {
        document
          .querySelector('meta[name="linkedin-url"]')
          ?.setAttribute('content', tenant.social_media_links.linkedin);
      }

      if (tenant.primary_color) {
        document
          .querySelector('meta[name="tenant-color"]')
          ?.setAttribute('content', tenant.primary_color);

        // Also update theme-color meta tag for browsers
        document
          .querySelector('meta[name="theme-color"]')
          ?.setAttribute('content', tenant.primary_color);
      }

      if (parsedLanguages.length > 0) {
        document
          .querySelector('meta[name="tenant-site-languages"]')
          ?.setAttribute('content', parsedLanguages.join(','));
      }

      // Return tenant info for API call
      return {
        tenantName: tenant.name || 'Skillio',
        tenantLogo: tenant.logo || DEFAULT_LOGO_PATH,
        tenantPrivacyUrl: tenant.privacy_statement || '',
        tenantTermUrl: tenant.general_terms || '',
        tenantFacebookUrl:
          (tenant.social_media_links && tenant.social_media_links.facebook) ||
          '',
        tenantXUrl:
          (tenant.social_media_links && tenant.social_media_links.twitter) ||
          '',
        tenantLinkedinUrl:
          (tenant.social_media_links && tenant.social_media_links.linkedin) ||
          '',
        tenantSiteLanguages: parsedLanguages,
        aiOperationCosts: parseAiOperationCosts(tenant.ai_operation_costs)
      };
    }
    return null;
  } catch (error) {
    const isCanceled =
      axios.isCancel(error) ||
      (error as AxiosError)?.code === 'ERR_CANCELED' ||
      (error instanceof DOMException && error.name === 'AbortError');
    if (isCanceled) {
      return null;
    }
    console.error('Error fetching tenant info from API:', error);
    return null;
  }
};

const getTenantFromApiShared = async (): Promise<TenantInfo | null> => {
  if (cachedTenantInfo) {
    return cachedTenantInfo;
  }

  if (!tenantRequestPromise) {
    tenantRequestPromise = fetchTenantFromApi().then((tenantInfo) => {
      if (tenantInfo) {
        cachedTenantInfo = tenantInfo;
      }
      return tenantInfo;
    });
  }

  return tenantRequestPromise;
};

export const useTenantInfo = (): TenantInfo => {
  const initialTenantInfo = buildTenantInfoFromMetaTags();
  const hasValidLogo = useRef<boolean>(!!initialTenantInfo.tenantLogo);

  const [tenantInfo, setTenantInfo] = useState<TenantInfo>(initialTenantInfo);

  const win = typeof window !== 'undefined';

  // Function to update tenant info from meta tags
  const getTenantInfoFromMetaTags = () => {
    // Get values from meta tags using the helper function
    const tenantName = getMetaContent('tenant-name');
    const tenantLogo = getMetaContent('tenant-logo');
    const tenantPrivacyUrl = getMetaContent('tenant-privacy-url');
    const tenantTermUrl = getMetaContent('tenant-term-url');
    const tenantFacebookUrl = getMetaContent('facebook-url');
    const tenantXUrl = getMetaContent('x-url');
    const tenantLinkedinUrl = getMetaContent('linkedin-url');
    const tenantSiteLanguagesStr = getMetaContent('tenant-site-languages');

    const tenantSiteLanguages = parseLanguages(tenantSiteLanguagesStr);
    const resolvedSiteLanguages =
      tenantSiteLanguages.length > 0
        ? tenantSiteLanguages
        : process.env.NODE_ENV === 'development'
          ? ['en', 'nl']
          : [];

    // Check if we found a valid logo
    if (tenantLogo && tenantLogo.trim() !== '') {
      hasValidLogo.current = true;
    }

    // Only update state if we have actual content to avoid unnecessary renders
    setTenantInfo((prevState) => {
      // If we already have a valid logo, don't replace it with default
      const logoToUse =
        hasValidLogo.current && prevState.tenantLogo
          ? prevState.tenantLogo
          : tenantLogo && tenantLogo.trim() !== ''
            ? tenantLogo
            : prevState.tenantLogo || '';

      return {
        ...prevState,
        tenantName: tenantName || prevState.tenantName,
        tenantLogo: logoToUse,
        tenantPrivacyUrl: tenantPrivacyUrl || prevState.tenantPrivacyUrl,
        tenantTermUrl: tenantTermUrl || prevState.tenantTermUrl,
        tenantFacebookUrl: tenantFacebookUrl || prevState.tenantFacebookUrl,
        tenantXUrl: tenantXUrl || prevState.tenantXUrl,
        tenantLinkedinUrl: tenantLinkedinUrl || prevState.tenantLinkedinUrl,
        tenantSiteLanguages:
          resolvedSiteLanguages.length > 0
            ? resolvedSiteLanguages
            : prevState.tenantSiteLanguages
      };
    });

    // Return true if we had logo content from meta tag
    return tenantLogo && tenantLogo.trim() !== '';
  };

  useEffect(() => {
    if (!win) return;

    const metaTagsHadLogo = getTenantInfoFromMetaTags();

    if (metaTagsHadLogo) {
      hasValidLogo.current = true;
    }

    let isUnmounted = false;

    getTenantFromApiShared().then((apiTenantInfo) => {
      if (isUnmounted) return;

      if (apiTenantInfo) {
        setTenantInfo((prevState) => ({
          ...prevState,
          tenantName: apiTenantInfo.tenantName || prevState.tenantName,
          tenantLogo: hasValidLogo.current
            ? prevState.tenantLogo
            : apiTenantInfo.tenantLogo || prevState.tenantLogo,
          tenantPrivacyUrl:
            apiTenantInfo.tenantPrivacyUrl || prevState.tenantPrivacyUrl,
          tenantTermUrl: apiTenantInfo.tenantTermUrl || prevState.tenantTermUrl,
          tenantFacebookUrl:
            apiTenantInfo.tenantFacebookUrl || prevState.tenantFacebookUrl,
          tenantXUrl: apiTenantInfo.tenantXUrl || prevState.tenantXUrl,
          tenantLinkedinUrl:
            apiTenantInfo.tenantLinkedinUrl || prevState.tenantLinkedinUrl,
          tenantSiteLanguages:
            apiTenantInfo.tenantSiteLanguages.length > 0
              ? apiTenantInfo.tenantSiteLanguages
              : prevState.tenantSiteLanguages,
          aiOperationCosts: apiTenantInfo.aiOperationCosts
        }));
      }

      if (apiTenantInfo?.tenantLogo && !hasValidLogo.current) {
        hasValidLogo.current = true;
      } else if (!hasValidLogo.current) {
        setTenantInfo((prevState) => ({
          ...prevState,
          tenantLogo: DEFAULT_LOGO_PATH
        }));
      }
    });

    return () => {
      isUnmounted = true;
    };
  }, [win]);

  // Set default logo if still empty after a delay
  useEffect(() => {
    if (!win) return;

    // If we still don't have a logo after all attempts, use default
    if (!tenantInfo.tenantLogo) {
      const timer = setTimeout(() => {
        setTenantInfo((prevState) => {
          // Only set default if it's still empty
          if (!prevState.tenantLogo) {
            return {
              ...prevState,
              tenantLogo: DEFAULT_LOGO_PATH
            };
          }
          return prevState;
        });
      }, 500); // Reduced wait time for better user experience

      return () => clearTimeout(timer);
    }
  }, [win, tenantInfo.tenantLogo]);

  return tenantInfo;
};
